import { useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { RequireRole } from "../../../../components/gate/RequireRole";
import { CORPUS_FEED_NAME_MAX_LENGTH } from "@shared/types/corpus/api.ts";
import { InlineEditableText } from "../../../../components/primitives/inputs/InlineEditableText";
import { CorpusFeedView } from "../../../../lib/corpusFeedView";
import { LibraryPage } from "../LibraryPage";
import { FeedActions, FeedBody } from "./FeedBody";
import { useFeedDetail } from "./useFeedDetail";
import { ROUTES } from "../../../../route_paths";

interface FeedPageProps {
  feedId: string;
}

function FeedPage({ feedId }: FeedPageProps) {
  const detail = useFeedDetail(feedId);
  const { feed } = detail;
  const view = feed.data ? new CorpusFeedView(feed.data, detail.statistics.byFeed[feedId]) : null;

  return (
    <LibraryPage
      title={
        view ? (
          <RequireRole role="platform.admin" fallback={view.name}>
            <InlineEditableText
              value={view.feed.name}
              display={() => view.name}
              label="Name"
              required
              maxLength={CORPUS_FEED_NAME_MAX_LENGTH}
              onSave={(name) => detail.saveDetails({ name })}
            />
          </RequireRole>
        ) : (
          "Feed"
        )
      }
      parents={[{ label: "Feeds", to: ROUTES.internal.library.feeds.path() }]}
      actions={
        view ? (
          <RequireRole role="platform.admin">
            <FeedActions
              feed={view.feed}
              runInProgress={view.openRun !== null}
              onChanged={detail.reloadAll}
            />
          </RequireRole>
        ) : null
      }
    >
      {feed.loading ? (
        <LoadingState />
      ) : feed.data ? (
        <FeedBody feed={feed.data} detail={detail} />
      ) : (
        <Alert severity="error">{feed.error ?? "This feed could not be found."}</Alert>
      )}
    </LibraryPage>
  );
}

/** One corpus feed: what it is, what it has brought in and its runs; an admin can also act on it. */
export default function FeedDetail() {
  const { id } = useParams();
  // Keyed by the id, so moving from one feed to another loads afresh.
  return id ? <FeedPage key={id} feedId={id} /> : null;
}
